/** Импорт → имя пакета Pyodide (loadPackage). */
export const PYODIDE_IMPORT_TO_PACKAGE: Record<string, string> = {
  numpy: "numpy",
  np: "numpy",
  matplotlib: "matplotlib",
  mpl_toolkits: "matplotlib",
  pandas: "pandas",
  pd: "pandas",
  scipy: "scipy",
  sympy: "sympy",
  sklearn: "scikit-learn",
  PIL: "Pillow",
  bs4: "beautifulsoup4",
  lxml: "lxml",
  micropip: "micropip",
  httpx: "httpx",
  json: "json",
  re: "re",
  math: "math",
  random: "random",
  bisect: "bisect",
  datetime: "datetime",
  collections: "collections",
  itertools: "itertools",
  functools: "functools",
  statistics: "statistics",
  fractions: "fractions",
  decimal: "decimal",
  typing: "typing",
  csv: "csv",
  hashlib: "hashlib",
  base64: "base64",
  urllib: "urllib",
  html: "html",
  xml: "xml",
  sqlite3: "sqlite3",
  gzip: "gzip",
  zlib: "zlib",
  uuid: "uuid",
  time: "time",
  os: "os",
  sys: "sys",
};

/** Стандартная библиотека Python — в Pyodide без loadPackage. */
export const PYTHON_STDLIB_MODULES = [
  "math",
  "bisect",
  "sys",
  "collections",
  "functools",
  "random",
  "itertools",
  "statistics",
  "json",
  "re",
  "datetime",
  "os",
  "time",
  "typing",
  "csv",
  "hashlib",
  "base64",
  "uuid",
  "decimal",
  "fractions",
  "copy",
  "enum",
  "string",
  "textwrap",
  "heapq",
  "operator",
  "pathlib",
  "io",
  "struct",
] as const;

export type PythonStdlibModule = (typeof PYTHON_STDLIB_MODULES)[number];

/** Подсказка в UI виджета «Код». */
export const PYTHON_STDLIB_HINT = [
  "math",
  "bisect",
  "sys",
  "collections",
  "functools",
  "random",
  "itertools",
  "statistics",
] as const;

const PYODIDE_BUILTINS = new Set<string>([
  "json",
  "re",
  "math",
  "random",
  "datetime",
  "collections",
  "itertools",
  "functools",
  "statistics",
  "fractions",
  "decimal",
  "typing",
  "csv",
  "hashlib",
  "base64",
  "urllib",
  "html",
  "xml",
  "sqlite3",
  "gzip",
  "zlib",
  "uuid",
  "time",
  "os",
  "sys",
  "io",
  "struct",
  "copy",
  "enum",
  "string",
  "textwrap",
  "bisect",
  "heapq",
  "operator",
  "pathlib",
  "tempfile",
  "shutil",
  "glob",
  "fnmatch",
  "pickle",
  "shelve",
  "traceback",
  "warnings",
  "contextlib",
  "abc",
  "dataclasses",
]);

export function isPythonStdlibModule(name: string): boolean {
  return PYODIDE_BUILTINS.has(name);
}

const PY_IMPORT_RE =
  /^\s*(?:import\s+([\w.]+(?:\s+as\s+\w+)?(?:\s*,\s*[\w.]+(?:\s+as\s+\w+)?)*)|from\s+([\w.]+)\s+import\s+)/gm;

/** Список пакетов Pyodide для предзагрузки по import в коде. */
export function parsePythonPackages(code: string): string[] {
  const roots = new Set<string>();
  let m: RegExpExecArray | null;
  PY_IMPORT_RE.lastIndex = 0;
  while ((m = PY_IMPORT_RE.exec(code)) !== null) {
    if (m[1]) {
      for (const part of m[1].split(",")) {
        const name = part.trim().split(/\s+as\s+/)[0]?.trim().split(".")[0];
        if (name) roots.add(name);
      }
    }
    if (m[2]) {
      const root = m[2].split(".")[0];
      if (root) roots.add(root);
    }
  }
  const packages = new Set<string>();
  for (const root of roots) {
    if (PYODIDE_BUILTINS.has(root)) continue;
    const pkg = PYODIDE_IMPORT_TO_PACKAGE[root] ?? root;
    if (pkg && !PYODIDE_BUILTINS.has(pkg)) packages.add(pkg);
  }
  if (/micropip\.install\s*\(/.test(code)) {
    packages.add("micropip");
  }
  return [...packages];
}

/** Разрешённые npm-модули для import в JavaScript (esm.sh). */
export const JS_IMPORT_TO_URL: Record<string, string> = {
  lodash: "https://esm.sh/lodash-es@4.17.21",
  "lodash-es": "https://esm.sh/lodash-es@4.17.21",
  "date-fns": "https://esm.sh/date-fns@4.1.0",
  mathjs: "https://esm.sh/mathjs@14.0.1",
  dayjs: "https://esm.sh/dayjs@1.11.13",
  uuid: "https://esm.sh/uuid@11.0.3",
  "chart.js": "https://esm.sh/chart.js@4.4.7",
  chartjs: "https://esm.sh/chart.js@4.4.7",
  three: "https://esm.sh/three@0.171.0",
  rxjs: "https://esm.sh/rxjs@7.8.1",
  "rxjs/operators": "https://esm.sh/rxjs@7.8.1/operators",
};

export function jsImportHint(): string {
  return Object.keys(JS_IMPORT_TO_URL)
    .filter((k) => !k.includes("/"))
    .slice(0, 12)
    .join(", ");
}

export function pythonImportHint(): string {
  const std = PYTHON_STDLIB_HINT.join(", ");
  const extra = ["numpy", "matplotlib", "pandas", "scipy", "sympy"].join(", ");
  return `${std} · ${extra}`;
}

export type JsModuleParts = {
  imports: string;
  body: string;
};

/** Разделить import/export-from и тело скрипта (import должен быть в начале модуля). */
export function splitJsModule(code: string): JsModuleParts {
  const lines = code.split("\n");
  const importLines: string[] = [];
  const bodyLines: string[] = [];
  let pastImports = false;
  for (const line of lines) {
    const t = line.trim();
    const isImport =
      t.startsWith("import ") ||
      (t.startsWith("export ") && /\bfrom\s+['"]/.test(t));
    if (!pastImports && (isImport || (importLines.length > 0 && t === ""))) {
      if (isImport || t === "") importLines.push(line);
      else {
        pastImports = true;
        bodyLines.push(line);
      }
    } else {
      pastImports = true;
      bodyLines.push(line);
    }
  }
  return { imports: importLines.join("\n").trim(), body: bodyLines.join("\n") };
}

export function rewriteJsImports(importBlock: string): string {
  if (!importBlock.trim()) return "";
  let result = importBlock.replace(
    /from\s+(['"])([^'"]+)\1/g,
    (_m, q: string, spec: string) => {
      if (/^https?:\/\//i.test(spec)) return `from ${q}${spec}${q}`;
      const url = JS_IMPORT_TO_URL[spec];
      if (!url) {
        throw new Error(
          `Модуль «${spec}» недоступен. Пример: import _ from 'lodash'. Доступны: ${jsImportHint()}`
        );
      }
      return `from ${q}${url}${q}`;
    }
  );
  result = result.replace(
    /import\s+(['"])([^'"]+)\1/g,
    (_m, q: string, spec: string) => {
      if (/^https?:\/\//i.test(spec)) return `import ${q}${spec}${q}`;
      const url = JS_IMPORT_TO_URL[spec];
      if (!url) {
        throw new Error(
          `Модуль «${spec}» недоступен. Пример: import 'lodash'. Доступны: ${jsImportHint()}`
        );
      }
      return `import ${q}${url}${q}`;
    }
  );
  return result;
}

export function buildJsModuleSource(code: string): string {
  const { imports, body } = splitJsModule(code);
  const rewritten = rewriteJsImports(imports);
  const consoleShim = `
const __logs = [];
const console = {
  log: (...a) => { __logs.push(a.map(x => typeof x === "string" ? x : JSON.stringify(x)).join(" ")); },
  info: (...a) => console.log(...a),
  warn: (...a) => { __logs.push("[warn] " + a.map(String).join(" ")); },
  error: (...a) => { __logs.push("[error] " + a.map(String).join(" ")); },
};
globalThis.__flushLogs = () => __logs.join("\\n");
`;
  const parts = [rewritten, consoleShim, body].filter(Boolean);
  return parts.join("\n\n");
}
